import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { ApiSuccess } from '../../lib/api';
import type { Book } from '../../hooks/useBooks';

const WORD_COUNT_OPTIONS = [
  { value: 30000 as const, label: '30K words', sub: '~10 chapters' },
  { value: 50000 as const, label: '50K words', sub: '~16 chapters' },
  { value: 80000 as const, label: '80K words', sub: '~22 chapters' },
];

interface Props {
  book: Book;
  onClose: () => void;
}

export function ContinueBookModal({ book, onClose }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [selectedWordCount, setSelectedWordCount] = useState<30000 | 50000 | 80000>(
    (book.targetWordCount as 30000 | 50000 | 80000) ?? 50000,
  );

  const { mutate: continueBook, isPending } = useMutation({
    mutationFn: async () => {
      const res = await api.post<ApiSuccess<{ bookId: string }>>(`/books/${book.id}/continue`, {
        targetWordCount: selectedWordCount,
      });
      return res.data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['books'] });
      void navigate(`/books/${book.id}`);
    },
    onError: (err: { response?: { data?: { message?: string }; status?: number } }) => {
      if (err.response?.status === 402) {
        setError('You need at least 1 credit to continue this book. Add credits or upgrade your plan.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    },
  });

  const lengthChanged = selectedWordCount !== book.targetWordCount;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-card border border-border rounded-xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-bold text-foreground mb-1">Continue Writing</h2>
        <p className="text-sm text-muted-foreground mb-5">
          Choose your book length and pick up where you left off.
        </p>

        {/* Book title */}
        <div className="mb-5">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Book</p>
          <p className="text-sm font-semibold text-foreground line-clamp-1">{book.title}</p>
          <p className="text-xs text-muted-foreground capitalize mt-0.5">{book.genre}</p>
        </div>

        {/* Book length selector */}
        <div className="mb-5">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Book Length</p>
          <div className="grid grid-cols-3 gap-2">
            {WORD_COUNT_OPTIONS.map(({ value, label, sub }) => (
              <button
                key={value}
                type="button"
                onClick={() => setSelectedWordCount(value)}
                className={`flex flex-col items-start p-3 border rounded-md transition-colors text-left ${
                  selectedWordCount === value
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-accent/50'
                }`}
              >
                <span className="font-medium text-foreground text-sm">{label}</span>
                <span className="text-xs text-muted-foreground">{sub}</span>
              </button>
            ))}
          </div>
          {lengthChanged && (
            <p className="text-xs text-muted-foreground mt-2">
              New length selected — outline will be regenerated to match.
            </p>
          )}
        </div>

        {/* Chapter summary */}
        <div className="flex items-center justify-between text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2.5 mb-4">
          <span>{book.chaptersComplete} chapter{book.chaptersComplete !== 1 ? 's' : ''} already written</span>
          <span className="text-foreground font-medium">1 credit</span>
        </div>

        {error && (
          <p className="text-sm text-destructive mb-4">{error}</p>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-border rounded-md text-sm text-muted-foreground hover:bg-accent transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => continueBook()}
            disabled={isPending}
            className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {isPending ? (
              <>
                <span className="h-3.5 w-3.5 rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground animate-spin" />
                Starting…
              </>
            ) : (
              'Continue Writing →'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
